import { ampli } from "@hl/shared-features/lib/ampli";
import { User } from "@hl/shared-features/lib/features/auth/User";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import {
  Anchor,
  Box,
  createStyles,
  Group,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { useElementSize, useMediaQuery } from "@mantine/hooks";

import { getMintPageUrl } from "~config";
import { useGetRelatedProjectsQuery } from "~features/MintPage/queries.graphql.generated";
import useMintState from "~hooks/useMintState";

const useCardStyles = createStyles(() => ({
  mediaItem: {
    minWidth: 55,
    minHeight: 55,
    width: 55,
    height: 55,
    alignItems: "center",
  },
  link: {
    textDecoration: "none",
  },
}));

type RelatedProjectProps = {
  collectionId: string;
  collectionName: string;
};

const RelatedProjects = ({
  collectionId,
  collectionName,
}: RelatedProjectProps) => {
  const { classes, theme } = useCardStyles();
  const { endedOrSoldOut, isAuction } = useMintState();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs - 1}px)`);
  const { ref, width } = useElementSize();
  const itemSize = isMobile
    ? "100%"
    : width > 2000
    ? "18%"
    : width > 1500
    ? "22%"
    : 300;

  const { data } = useGetRelatedProjectsQuery({
    variables: {
      collectionId,
      first: 9,
      after: null,
    },
  });

  const relatedProjects = data?.getRelatedCollections.collections.filter(
    (project) => project.id.toLowerCase() !== collectionId.toLowerCase()
  );

  if (!relatedProjects?.length) return <></>;

  return (
    <Box ref={ref}>
      <ScrollArea
        offsetScrollbars
        sx={{
          padding: isMobile ? "20px" : "40px",
          margin: isMobile ? "0 -20px" : "0 -40px",
          borderTop:
            endedOrSoldOut && !isAuction
              ? undefined
              : `1px solid ${theme.colors.divider[0]}`,
          borderBottom: `1px solid ${theme.colors.divider[0]}`,
          backgroundColor: theme.colors.secondaryBackground[0],
        }}
        styles={{
          viewport: {
            padding: isMobile ? "0 20px" : "0 40px",
            margin: isMobile ? "0 -20px" : "0 -40px",
            width: "unset",
          },
        }}
      >
        <Group noWrap spacing={isMobile ? 24 : 32}>
          <Text size={isMobile ? "md" : "lg"} sx={{ flexShrink: 0 }}>
            Related projects
          </Text>
          {relatedProjects.map((project) => (
            <Group
              spacing={0}
              w={itemSize}
              noWrap
              miw={250}
              key={project.id}
              sx={{ border: `0.25px solid ${theme.colors.divider[0]}` }}
            >
              <Box className={classes.mediaItem}>
                <ResizedImage
                  src={project.collectionImage}
                  fit="cover"
                  aspectRatio="1"
                />
              </Box>
              <Stack px={20} justify="center" spacing={2} w="100%">
                {/*TODO: <Link /> is faster, but we have to fix the native browser scroll position restoration*/}
                <Anchor
                  className={classes.link}
                  onClick={() => {
                    if (!ampli.isLoaded) {
                      return;
                    }
                    ampli.relatedProjectClick({
                      projectName: collectionName,
                      relatedProjectName: project.name,
                    });
                  }}
                  sx={{ textDecoration: "none !important" }}
                  href={getMintPageUrl(project, { withBaseUrl: true })}
                >
                  <Text size="sm" lineClamp={1}>
                    {project.name}
                  </Text>
                </Anchor>
                <User
                  walletAddresses={
                    project.creatorAccountSettings?.ensNames?.length
                      ? project.creatorAccountSettings?.ensNames
                      : project.creatorAccountSettings?.walletAddresses
                  }
                  displayName={project.creatorAccountSettings?.displayName}
                  verified={!!project.creatorAccountSettings?.verified}
                  avatarUrl={project.creatorAccountSettings?.displayAvatar}
                  imported={!!project.creatorAccountSettings?.imported}
                  textSize="xs"
                />
              </Stack>
            </Group>
          ))}
        </Group>
      </ScrollArea>
    </Box>
  );
};

export default RelatedProjects;
